import { inject, isDevMode } from '@angular/core';
import { HttpInterceptorFn, HttpResponse, HttpErrorResponse } from '@angular/common/http';
import { catchError, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { ClarityService } from '../../services/clarity.service';
import { buildSuccessEventName, buildFailureEventName } from './clarity-event.util';

/**
 * Logs every HTTP request/response as a Microsoft Clarity timeline event,
 * per the API Event Logs spec (Section 4):
 *  - 2xx responses  -> api_success_[METHOD]_[sanitizedPath]
 *  - non-2xx errors -> api_fail_[METHOD]_[sanitizedPath]_code_[statusCode]_msg_[sanitizedErrorMessage]
 *  - network/timeout (status 0) -> api_fail_[METHOD]_[sanitizedPath]_code_0_msg_[sanitizedException]
 */
export const clarityInterceptor: HttpInterceptorFn = (req, next) => {
  const clarityService = inject(ClarityService);
  const method = req.method;
  const path = relativePath(req.url);

  return next(req).pipe(
    tap((event) => {
      if (event instanceof HttpResponse && event.status >= 200 && event.status < 300) {
        const eventName = buildSuccessEventName(method, path);
        clarityService.trackEvent(eventName);
        if (isDevMode()) {
          console.debug('[Clarity] event:', eventName);
        }
      }
    }),
    catchError((err: unknown) => {
      if (err instanceof HttpErrorResponse) {
        let eventName: string;
        if (err.status === 0) {
          // Network-level / timeout failure — no HTTP status was received.
          const exceptionMsg = err.message || err.statusText || 'NetworkError';
          eventName = buildFailureEventName(method, path, 0, exceptionMsg);
        } else {
          const errorMsg = extractErrorMessage(err);
          eventName = buildFailureEventName(method, path, err.status, errorMsg);
        }
        clarityService.trackEvent(eventName);
        if (isDevMode()) {
          console.debug('[Clarity] event:', eventName);
        }
      }
      return throwError(() => err);
    }),
  );
};

function relativePath(url: string): string {
  try {
    const parsed = new URL(url, typeof window !== 'undefined' ? window.location.origin : undefined);
    return parsed.pathname;
  } catch {
    return url;
  }
}

function extractErrorMessage(err: HttpErrorResponse): string {
  const body: any = err.error;
  if (typeof body === 'string') return body;
  if (body?.message) return body.message;
  if (body?.detail) return body.detail;
  if (body?.error) return typeof body.error === 'string' ? body.error : JSON.stringify(body.error);
  return err.statusText || 'unknown_error';
}
